/**
 * ヘッダー メガメニュー
 *
 * .ist-js__headerMegaMenuLink をクリックするとメガメニューが開きます
 */
// HeaderMegaMenu.ts
import { HeaderMenu, Link, MenuSection } from './_type';

/**
 * メガメニュー フォーカストラップ
 */
class MegaMenuFocusTrap {
  private element: HTMLElement;
  private lastFocus: HTMLElement | null = null;
  private observer: MutationObserver;
  private isActive: boolean = false;
  private keydownHandler: ((event: KeyboardEvent) => void) | null = null;

  /**
   * フォーカストラップを初期化
   * @param element フォーカストラップを適用する要素
   */
  constructor(element: HTMLElement) {
    this.element = element;
    this.observer = new MutationObserver(() => {
      if (this.isActive) {
        this.updateFocusableElements();
      }
    });
  }

  /**
   * フォーカストラップを有効化
   */
  activate(): void {
    this.isActive = true;
    this.lastFocus = document.activeElement as HTMLElement;
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden'],
    });

    this.updateFocusableElements();
  }

  /**
   * フォーカストラップを無効化
   */
  deactivate(): void {
    this.isActive = false;
    this.observer.disconnect();
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
    this.lastFocus?.focus();
  }

  /**
   * フォーカス可能な要素を取得
   * @returns フォーカス可能な要素の配列
   */
  private getFocusableElements(): HTMLElement[] {
    const menuContainer = this.element.querySelector(
      '.ist-header__megaMenu--open'
    );
    if (!menuContainer) return [];

    // メガメニュー内のフォーカス可能な要素のみを取得
    return Array.from(
      menuContainer.querySelectorAll<HTMLElement>(
        'a[href]:not([disabled]), button:not([disabled]), [tabindex="0"]'
      )
    ).filter((el) => {
      const style = window.getComputedStyle(el);
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        !el.hasAttribute('aria-hidden')
      );
    });
  }

  /**
   * フォーカス可能な要素を更新し、キーボードイベントを設定
   */
  private updateFocusableElements(): void {
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
    }

    const focusableElements = this.getFocusableElements();
    if (!focusableElements.length) return;

    this.keydownHandler = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !this.isActive) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (!activeElement) return;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', this.keydownHandler);
  }
}

/**
 * メガメニュー
 */
class HeaderMegaMenu {
  private static readonly HOVER_DELAY = 50; // ms
  private static LINK_JSON: HeaderMenu | null = null; // 静的プロパティを変更
  private currentMenu: HTMLElement | null = null;
  private menuContainer: HTMLElement | null = null;
  private fetchUrl: string; // Fetch先のURLを保持
  private eventListeners: Array<{
    element: HTMLElement;
    event: string;
    handler: EventListener;
  }> = [];
  private focusTrap: MegaMenuFocusTrap | null = null;

  /**
   * メガメニューを初期化
   */
  constructor(fetchUrl: string = '/assets/json/header.json') {
    this.fetchUrl = fetchUrl; // Fetch先のURLを設定
    if (this.fetchUrl === '/assets/json/header.json') {
      // <html /> の lang 属性を取得
      const htmlElement = document.querySelector('html');
      if (htmlElement instanceof HTMLElement) {
        const lang = htmlElement.getAttribute('lang');
        if (lang === 'en') {
          this.fetchUrl = '/assets/json/header-en.json';
        }
      }
    }
    const headerElement = document.querySelector('.ist-header');
    if (headerElement instanceof HTMLElement) {
      this.focusTrap = new MegaMenuFocusTrap(headerElement);

      // '.ist-header' が data-lang="en" を持っている場合は、jsonUrl と lang を変更
      if (headerElement.getAttribute('data-lang') === 'en') {
        this.fetchUrl = '/assets/json/header-en.json';
      }
    }

    this.menuContainer = document.querySelector(
      '.ist-header__megaMenuContainer'
    );
    if (!this.menuContainer) return;

    // 既存のメニューをクリア
    this.menuContainer.innerHTML = '';

    // JSONを読み込んでからメニューを初期化
    this.loadMenuData();
  }

  /**
   * メニューデータを読み込む
   */
  private async loadMenuData(): Promise<void> {
    try {
      const response = await fetch(this.fetchUrl);
      if (!response.ok) {
        throw new Error('Failed to load menu data');
      }
      HeaderMegaMenu.LINK_JSON = await response.json();
      // _commentout: true のデータを削除
      HeaderMegaMenu.LINK_JSON = this.filterMenuData(HeaderMegaMenu.LINK_JSON);
      this.init();
    } catch (error) {
      console.error('Error loading menu data:', error);
    }
  }

  /**
   * _commentout: true のデータを削除（メニュー全体）
   */
  private filterMenuData(menuData: HeaderMenu | null): MenuSection[] {
    if (!menuData) return [];
    return (
      menuData
        //.filter((section) => !section._commentout) // セクションレベルで削除
        .map((section) => ({
          ...section,
          links: section.links ? this.filterLinks(section.links) : [], // 再帰的に適用
        }))
    );
  }

  /**
   * _commentout: true のリンクを削除（リンクリストを再帰的に処理）
   */
  private filterLinks(links: Link[]): Link[] {
    return links
      .filter((link) => !link._commentout) // _commentout: true のリンクを削除
      .map((link) => ({
        ...link,
        links: link.links ? this.filterLinks(link.links) : [], // 再帰的に処理
      }))
      .filter(
        (link) =>
          link.links.length > 0 ||
          (link.link !== undefined && link.link !== null)
      );
  }

  /**
   * メガメニューを破棄
   */
  public destroy(): void {
    this.eventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.eventListeners = [];

    if (this.menuContainer) {
      this.menuContainer.innerHTML = '';
    }

    this.currentMenu = null;
  }

  /**
   * メガメニューの初期化処理
   */
  private init(): void {
    if (!HeaderMegaMenu.LINK_JSON) return;
    this.createMenus();
    this.addEvent();
  }

  /**
   * 全階層のcurrentを解除
   */
  private clearCurrentState(): void {
    // 全階層のcurrentを解除
    document
      .querySelectorAll('.ist-header__megaMenuLink--current')
      .forEach((element) => {
        element.classList.remove('ist-header__megaMenuLink--current');
      });
  }

  /**
   * イベントリスナーを追加し、クリーンアップ用に保存
   * @param element イベントを追加する要素
   * @param event イベント名
   * @param handler イベントハンドラー
   */
  private addEventListenerWithCleanup<K extends keyof HTMLElementEventMap>(
    element: HTMLElement,
    event: K,
    handler: (ev: HTMLElementEventMap[K]) => void
  ): void {
    element.addEventListener(event, handler as EventListener);
    this.eventListeners.push({
      element,
      event,
      handler: handler as EventListener,
    });
  }

  /**
   * メガメニューの項目に対してホバーとキーボード操作の挙動を設定
   * @param elements 対象となる要素の配列
   * @param callback 実行されるコールバック関数
   */
  private setupHoverBehavior(
    elements: HTMLAnchorElement[],
    callback: (element: HTMLElement, index: number) => void
  ): void {
    elements.forEach((element, index) => {
      let isMouseOver = false;
      let moveTimer: ReturnType<typeof setTimeout> | null = null;
      let lastMoveTime = 0;

      const handleMouseMove = () => {
        lastMoveTime = Date.now();

        if (moveTimer) {
          clearTimeout(moveTimer);
        }

        moveTimer = setTimeout(() => {
          const currentTime = Date.now();
          if (
            currentTime - lastMoveTime >= HeaderMegaMenu.HOVER_DELAY &&
            isMouseOver
          ) {
            // すでに対象の要素がcurrentクラスを持っている場合はスキップ
            if (
              element.classList.contains('ist-header__megaMenuLink--current')
            ) {
              return;
            }

            // アクティブな要素からフォーカスを外す
            const activeElement = document.activeElement as HTMLElement;
            if (activeElement && activeElement !== document.body) {
              activeElement.blur();
            }

            callback(element, index);
          }
        }, HeaderMegaMenu.HOVER_DELAY);
      };

      // マウスホバー時の処理
      this.addEventListenerWithCleanup(element, 'mouseenter', () => {
        isMouseOver = true;
        handleMouseMove();
      });

      this.addEventListenerWithCleanup(element, 'mouseleave', () => {
        isMouseOver = false;
        if (moveTimer) {
          clearTimeout(moveTimer);
          moveTimer = null;
        }
      });

      this.addEventListenerWithCleanup(element, 'mousemove', handleMouseMove);

      // クリックとキーボード操作時の共通処理を修正
      const handleAction = (e: Event) => {
        // リンクの動作は常に維持
        // サブメニューの表示のみを行う
        callback(element, index);
      };

      // マウスクリック時の処理
      this.addEventListenerWithCleanup(element, 'click', handleAction);

      // キーボード操作時の処理
      this.addEventListenerWithCleanup(
        element,
        'keydown',
        (e: KeyboardEvent) => {
          if (e.key === 'Enter') {
            handleAction(e);
          }
        }
      );
    });
  }

  /**
   * メガメニューのHTMLを生成
   */
  private createMenus(): void {
    if (!this.menuContainer || !HeaderMegaMenu.LINK_JSON) return;

    HeaderMegaMenu.LINK_JSON.forEach((menuData: MenuSection) => {
      const menuHtml = this.createMenuHtml(menuData);
      this.menuContainer?.appendChild(menuHtml);
    });
  }

  /**
   * メニューセクションのHTMLを生成
   * @param menuData メニューセクションのデータ
   * @returns 生成されたHTML要素
   */
  private createMenuHtml(menuData: MenuSection): HTMLElement {
    const menuContainer = document.createElement('div');
    const menuId = menuData.id.replace('#', '');
    menuContainer.id = menuId;
    menuContainer.className = 'ist-header__megaMenu';
    menuContainer.setAttribute('role', 'navigation');
    menuContainer.setAttribute('aria-hidden', 'true');
    menuContainer.setAttribute('aria-label', `${menuData.title}のメニュー`);

    menuContainer.innerHTML = `
      <div class="ist-header__megaMenuInner">
        <div class="ist-header__megaMenuHeader">
          <div class="ist-header__megaMenuHeaderInner">
            <button class="header__megaMenuHeaderClose" aria-label="メニューを閉じる" type="button">
              <i class="ist-icon ist-icon--close24" aria-hidden="true"></i>
            </button>
          </div>
        </div>
        <div class="ist-header__megaMenuContent">
          <div class="ist-header__megaMenuContentInner">
            <div class="ist-header__megaMenuContentItem ist-header__megaMenuContentItem--01" id="${menuId}-level1">
              <ul class="ist-header__megaMenuLinkList">
              ${(menuData.links || [])
                .map((link, index) => {
                  const hasSubLinks = link.links && link.links.length > 0;
                  return `
                    <li class="ist-header__megaMenuLinkItem">
                      <a href="${link.link}" 
                         class="ist-header__megaMenuLink ${
                           link.isCaption
                             ? 'ist-header__megaMenuLink--caption'
                             : ''
                         }"
                         ${link.target ? `target="${link.target}"` : ''}
                         ${hasSubLinks ? `aria-expanded="false"` : ''}
                         ${hasSubLinks ? `aria-controls="${menuId}-level2"` : ''}>
                        ${link.title}
                      </a>
                    </li>
                  `;
                })
                .join('')}
              </ul>
            </div>
            <div class="ist-header__megaMenuContentItem ist-header__megaMenuContentItem--02" id="${menuId}-level2"></div>
            <div class="ist-header__megaMenuContentItem ist-header__megaMenuContentItem--03" id="${menuId}-level3"></div>
            <div class="ist-header__megaMenuContentItem ist-header__megaMenuContentItem--04"></div>
          </div>
        </div>
      </div>
    `;

    const mainLinks = Array.from(
      menuContainer.querySelectorAll<HTMLAnchorElement>(
        '.ist-header__megaMenuContentItem--01 .ist-header__megaMenuLink'
      )
    );

    this.setupHoverBehavior(mainLinks, (link: HTMLElement, index: number) => {
      // 既に選択済みの場合は処理をスキップ
      if (link.classList.contains('ist-header__megaMenuLink--current')) {
        return;
      }

      mainLinks.forEach((l) => {
        l.classList.remove('ist-header__megaMenuLink--current');
        l.setAttribute('aria-expanded', 'false');
      });
      link.classList.add('ist-header__megaMenuLink--current');
      link.setAttribute('aria-expanded', 'true');

      const linkData = menuData.links?.[index];
      if (!linkData?.links || linkData?.links.length === 0) {
        // 次の階層のリンクがない場合、2階層目と3階層目をクリア
        const subMenuContainer = menuContainer.querySelector<HTMLElement>(
          '.ist-header__megaMenuContentItem--02'
        );
        const detailContainer = menuContainer.querySelector<HTMLElement>(
          '.ist-header__megaMenuContentItem--03'
        );
        if (subMenuContainer) {
          subMenuContainer.innerHTML = '';
        }
        if (detailContainer) {
          detailContainer.innerHTML = '';
        }
        return;
      }

      this.showSubLinks(menuContainer, linkData);
    });

    const closeButton = menuContainer.querySelector<HTMLButtonElement>(
      '.header__megaMenuHeaderClose'
    );
    if (closeButton) {
      const closeHandler = () => this.closeMenu(menuContainer);
      const keydownHandler = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.closeMenu(menuContainer);
        }
      };

      this.addEventListenerWithCleanup(closeButton, 'click', closeHandler);
      this.addEventListenerWithCleanup(closeButton, 'keydown', keydownHandler);
    }

    return menuContainer;
  }

  /**
   * サブリンクを表示
   * @param menuContainer メニューコンテナ要素
   * @param link リンクデータ
   */
  private showSubLinks(menuContainer: HTMLElement, link: Link): void {
    const subMenuContainer = menuContainer.querySelector<HTMLElement>(
      '.ist-header__megaMenuContentItem--02'
    );
    const detailContainer = menuContainer.querySelector<HTMLElement>(
      '.ist-header__megaMenuContentItem--03'
    );
    if (!subMenuContainer || !link.links) return;

    // 現在表示されているリンクと新しく表示しようとしているリンクが同じ場合はスキップ
    const currentLinks = Array.from(
      subMenuContainer.querySelectorAll('.ist-header__megaMenuLink')
    ).map((link) => link.textContent?.trim());
    const newLinks = link.links.map((l) => l.title);

    if (JSON.stringify(currentLinks) === JSON.stringify(newLinks)) {
      return;
    }

    // 2階層目のリンクのaria-expandedを全てfalseに設定
    subMenuContainer
      .querySelectorAll('.ist-header__megaMenuLink')
      .forEach((element) => {
        if (element.hasAttribute('aria-expanded')) {
          element.setAttribute('aria-expanded', 'false');
        }
        element.classList.remove('ist-header__megaMenuLink--current');
      });

    // 3階層目のコンテナをクリア
    if (detailContainer) {
      detailContainer.innerHTML = '';
    }

    subMenuContainer.innerHTML = `
    <ul class="ist-header__megaMenuLinkList">
      ${link.links
        .map((subLink, index) => {
          const hasSubLinks = subLink.links && subLink.links.length > 0;
          const menuId = menuContainer.id;
          return `
            <li class="ist-header__megaMenuLinkItem">
              <a href="${subLink.link}" 
                 class="ist-header__megaMenuLink ${subLink.isCaption ? 'ist-header__megaMenuLink--caption' : ''}"
                 ${subLink.target ? `target="${subLink.target}"` : ''}
                 ${hasSubLinks ? `aria-expanded="false"` : ''}
                 ${hasSubLinks ? `aria-controls="${menuId}-level3"` : ''}>
                ${subLink.title}
              </a>
            </li>
          `;
        })
        .join('')}
    </ul>
  `;

    // アニメーション用クラスを追加
    const linkList = subMenuContainer.querySelector(
      '.ist-header__megaMenuLinkList'
    );
    if (linkList) {
      requestAnimationFrame(() => {
        linkList.classList.add('ist-header__megaMenuLinkList--created');
      });
    }

    const subLinks = Array.from(
      subMenuContainer.querySelectorAll<HTMLAnchorElement>(
        '.ist-header__megaMenuLink'
      )
    );

    this.setupHoverBehavior(subLinks, (subLink: HTMLElement) => {
      // 既に選択済みの場合は処理をスキップ
      if (subLink.classList.contains('ist-header__megaMenuLink--current')) {
        return;
      }

      // 同階層の全リンクの状態を初期化
      subLinks.forEach((l) => {
        l.classList.remove('ist-header__megaMenuLink--current');
        if (l.hasAttribute('aria-expanded')) {
          l.setAttribute('aria-expanded', 'false');
        }
      });

      // 選択されたリンクの状態を更新
      subLink.classList.add('ist-header__megaMenuLink--current');

      // subLinkDataを先に取得し、存在チェック
      const subLinkData = link.links?.find(
        (l) => l.title === subLink.textContent?.trim()
      );

      if (subLinkData) {
        const hasSubLinks = subLinkData.links && subLinkData.links.length > 0;
        if (hasSubLinks && subLink.hasAttribute('aria-expanded')) {
          subLink.setAttribute('aria-expanded', 'true');
        }

        if (detailContainer && subLinkData.links) {
          this.showDetailLinks(detailContainer, subLinkData.links);
        } else if (detailContainer) {
          detailContainer.innerHTML = '';
        }
      }
    });
  }

  /**
   * 詳細リンクを表示
   * @param container 詳細リンクを表示するコンテナ要素
   * @param links リンクデータの配列
   */
  private showDetailLinks(container: HTMLElement, links: Link[]): void {
    container.innerHTML = `
      <ul class="ist-header__megaMenuLinkList">
        ${links
          .map(
            (detailLink) => `
            <li class="ist-header__megaMenuLinkItem">
              <a href="${detailLink.link}" 
                 class="ist-header__megaMenuLink ${
                   detailLink.isCaption
                     ? 'ist-header__megaMenuLink--caption'
                     : ''
                 }"
                 ${detailLink.target ? `target="${detailLink.target}"` : ''}>
                ${detailLink.title}
              </a>
            </li>
          `
          )
          .join('')}
      </ul>
    `;

    // アニメーション用クラスを追加
    const linkList = container.querySelector('.ist-header__megaMenuLinkList');
    if (linkList) {
      requestAnimationFrame(() => {
        linkList.classList.add('ist-header__megaMenuLinkList--created');
      });
    }
  }

  /**
   * メガメニューのイベントを設定
   */
  private addEvent(): void {
    const links = document.querySelectorAll<HTMLAnchorElement>(
      '.ist-js__headerMegaMenuLink'
    );

    links.forEach((link: HTMLAnchorElement) => {
      const href = link.getAttribute('href');
      // #から始まるリンクの場合のみメガメニュー処理を行う
      if (href?.startsWith('#')) {
        const id = href.split('#')[1] || '';
        const menu = document.getElementById(id);

        if (menu) {
          // aria属性の設定
          link.setAttribute('aria-expanded', 'false');
          link.setAttribute('aria-controls', id);

          const clickHandler = (e: Event) => {
            e.preventDefault();
            if (menu) {
              this.handleClick(e, menu);
              return false;
            }
          };

          this.addEventListenerWithCleanup(link, 'click', clickHandler);
        }
      }
    });

    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.currentMenu) {
        this.closeMenu(this.currentMenu);
      }
    };

    this.addEventListenerWithCleanup(
      document as unknown as HTMLElement,
      'keydown',
      escHandler
    );
  }

  /**
   * クリックイベントを処理
   * @param e イベントオブジェクト
   * @param menu メニュー要素
   */
  private handleClick(e: Event, menu: HTMLElement): void {
    if (this.currentMenu && this.currentMenu !== menu) {
      this.closeMenu(this.currentMenu);
    }
    this.toggleMenu(menu);
  }

  /**
   * メニューの表示/非表示を切り替え
   * @param menuElement メニュー要素
   */
  private toggleMenu(menuElement: HTMLElement): void {
    const isOpen = menuElement.classList.contains('ist-header__megaMenu--open');
    if (isOpen) {
      this.closeMenu(menuElement);
    } else {
      this.openMenu(menuElement);
    }
  }

  /**
   * メニューを開く
   * @param menuElement メニュー要素
   */
  private openMenu(menuElement: HTMLElement): void {
    menuElement.classList.add('ist-header__megaMenu--open');
    menuElement.setAttribute('aria-hidden', 'false');

    // アニメーションように時差をつけて
    setTimeout(() => {
      menuElement.classList.add('ist-header__megaMenu--animate');

      // ヘッダーリンクにフォーカスを移動
      const headerLink = menuElement.querySelector<HTMLAnchorElement>(
        '.header__megaMenuHeaderLink'
      );
      if (headerLink) {
        headerLink.focus({ preventScroll: true });
      }
    }, 150);

    // バナーの表示処理
    const menuId = menuElement.id;
    const menuData = HeaderMegaMenu.LINK_JSON?.find(
      (menu) => menu.id === `${menuId}`
    );
    const bannerContainer = menuElement.querySelector(
      '.ist-header__megaMenuContentItem--04'
    );

    if (bannerContainer && menuData?.banners) {
      bannerContainer.innerHTML = `
        <ul class="ist-header__megaMenuLinkList">
          ${menuData.banners
            .map(
              (banner) => `
            <li class="ist-header__megaMenuLinkItem">
              <a href="${banner.link}" class="ist-header__megaMenuBannerLink">
                <figure class="ist-header__megaMenuBanner">
                  <img src="${banner.image}" alt="">
                  <figcaption class="ist-header__megaMenuBannerCaption">
                    ${banner.title}
                  </figcaption>
                </figure>
              </a>
            </li>
          `
            )
            .join('')}
        </ul>
      `;
    }

    // フォーカストラップを有効化
    this.focusTrap?.activate();

    // ヘッダーにアクティブクラスを追加
    const headerContainer = document.querySelector('.ist-header');
    headerContainer?.classList.add('ist-header--active');

    // トリガーにアクティブクラスを追加
    const triggerId = menuElement.id;
    const trigger = document.querySelector(`[aria-controls="${triggerId}"]`);
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
      trigger.classList.add('ist-header__megaMenuLink--active');
      // 親要素にアクティブクラスを追加
      const parentNavItem = trigger.closest('.ist-header__row02NavItem');
      parentNavItem?.classList.add('ist-header__row02NavItem--active');
    }

    this.currentMenu = menuElement;
  }

  /**
   * メニューを閉じる
   * @param menuElement メニュー要素
   */
  private closeMenu(menuElement: HTMLElement): void {
    // メニュー本体の状態を初期化
    menuElement.classList.remove('ist-header__megaMenu--animate');
    menuElement.setAttribute('aria-hidden', 'true');

    // アニメーションように時差をつけて
    setTimeout(() => {
      menuElement.classList.remove('ist-header__megaMenu--open');

      // 各階層のコンテンツをクリア
      const level2 = menuElement.querySelector<HTMLElement>(
        '.ist-header__megaMenuContentItem--02'
      );
      const level3 = menuElement.querySelector<HTMLElement>(
        '.ist-header__megaMenuContentItem--03'
      );
      if (level2) level2.innerHTML = '';
      if (level3) level3.innerHTML = '';

      // すべてのメニュー項目のアクティブ状態とaria属性を初期化
      menuElement
        .querySelectorAll('.ist-header__megaMenuLink')
        .forEach((link) => {
          link.classList.remove('ist-header__megaMenuLink--current');
          link.setAttribute('aria-expanded', 'false');
        });
    }, 150);

    // フォーカストラップを無効化
    this.focusTrap?.deactivate();

    // ヘッダーのアクティブ状態を解除
    const header = document.querySelector('.ist-header');
    header?.classList.remove('ist-header--active');

    // トリガーの状態を初期化
    const triggerId = menuElement.id;
    const trigger = document.querySelector(`[aria-controls="${triggerId}"]`);
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.classList.remove('ist-header__megaMenuLink--active');
      // 親要素からアクティブクラスを削除
      const parentNavItem = trigger.closest('.ist-header__row02NavItem');
      parentNavItem?.classList.remove('ist-header__row02NavItem--active');
    }

    // グローバルな状態をリセット
    if (this.currentMenu === menuElement) {
      this.currentMenu = null;
    }
  }
}

export default HeaderMegaMenu;
